// Fairness aggregation for the dashboard panel. Sums parentForYear()
// outcomes across multiple years to produce a directional signal: how
// many holidays each parent has, year-by-year breakdown, and imbalance
// drivers (fixed-parent holidays that account for the spread).
//
// Counts holidays as equal units, NOT as actual days. Spring break and
// Christmas Day each count as one item — durational accuracy is a future
// enhancement. The current count is enough for parents to spot lopsided
// rules at a glance.

import { parentForYear } from './proseRenderer.js'

function blankBucket() {
  return { A: 0, B: 0, shared: 0, base: 0, flex: 0, unset: 0 }
}

// Returns:
// {
//   totals: { A, B, shared, base, flex, unset },     // sums across all years
//   perYear: { [year]: { A, B, ... } },              // same shape per year
//   aShare, bShare,                                  // A + 0.5*shared, B + 0.5*shared
//   spread,                                          // |aShare - bShare|, rounded
//   dominant: 'A' | 'B' | null,                      // null when spread <= 5
//   drivers: [{ id, name }, ...]                     // up to 3 holidays consistently with the dominant parent
// }
export function computeFairness(holidays, years) {
  const totals = blankBucket()
  const perYear = {}
  for (const y of years) perYear[y] = blankBucket()

  for (const h of holidays) {
    for (const y of years) {
      const who = parentForYear(h.rule, y)
      if (who === 'A') {
        totals.A += 1
        perYear[y].A += 1
      } else if (who === 'B') {
        totals.B += 1
        perYear[y].B += 1
      } else if (who === 'shared' || who === 'split' || who === 'rotation') {
        // Shared / split / rotation: both parents get partial time. We track
        // the count separately so the bar can show a "shared" segment AND
        // we can fold half-credit into each parent's headline number.
        totals.shared += 1
        perYear[y].shared += 1
      } else if (who === 'base') {
        totals.base += 1
        perYear[y].base += 1
      } else if (who === 'flex') {
        totals.flex += 1
        perYear[y].flex += 1
      } else {
        totals.unset += 1
        perYear[y].unset += 1
      }
    }
  }

  const aShare = totals.A + totals.shared * 0.5
  const bShare = totals.B + totals.shared * 0.5
  const spread = Math.abs(aShare - bShare)

  // Drivers: fixed-parent holidays that land on the dominant parent every
  // year in the window. We only surface this when the spread is meaningful
  // (>5 across the 5-year window) so we don't draw attention to noise.
  let dominant = null
  let drivers = []
  if (spread > 5) {
    dominant = aShare > bShare ? 'A' : 'B'
    drivers = holidays
      .filter((h) => years.every((y) => parentForYear(h.rule, y) === dominant))
      .map((h) => ({ id: h.id, name: h.name }))
      .slice(0, 3)
  }

  return { totals, perYear, aShare, bShare, spread, dominant, drivers }
}
